import {
  AimOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  LineOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useFormItemEnabled } from '@framework/form/item/useFormItemEnabled.ts';
import { Radio } from 'antd';
import { type TFunction } from 'i18next';
import { type ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MapWidgetSelectionShape } from '../IMapWidget.ts';
import { MapDrawingRadioButton } from './radio-button/MapDrawingRadioButton.tsx';
import { type MapDrawingMode } from './useMapDrawingInteractions.tsx';

/**
 * Label per la tipologia di shape, usata nel Segmented
 * di selezione shape.
 */
function getShapeLabel(t: TFunction, shape: MapWidgetSelectionShape): string {
  switch (shape) {
    case MapWidgetSelectionShape.Point:
      return t('map-widget.draw.shape.point.label', 'Punto');
    case MapWidgetSelectionShape.Line:
      return t('map-widget.draw.shape.line.label', 'Linea');
    case MapWidgetSelectionShape.Polygon:
      return t('map-widget.draw.shape.polygon.label', 'Poligono');
  }
}

/**
 * Icona associata a ciascun tipo di geometria, coerente con i controlli di
 * interrogazione (punto, linea, poligono).
 */
function getShapeIcon(shape: MapWidgetSelectionShape): ReactNode {
  switch (shape) {
    case MapWidgetSelectionShape.Point:
      return <AimOutlined aria-hidden="true" />;
    case MapWidgetSelectionShape.Line:
      return <LineOutlined aria-hidden="true" />;
    case MapWidgetSelectionShape.Polygon:
      return <EditOutlined aria-hidden="true" />;
  }
}

const DrawingControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export interface IMapDrawingControlsProps {
  onModeChange: (mode: MapDrawingMode) => void;
  mode: MapDrawingMode;
  showIdentify?: boolean;
  isMobile?: boolean | null;
  /** Tipologie di geometria abilitate nella configurazione. */
  shapes: MapWidgetSelectionShape[];
  /** Tipologia di disegno attualmente selezionata. */
  activeShape: MapWidgetSelectionShape;
  /** Callback per il cambio di tipologia di disegno. */
  onActiveShapeChange: (shape: MapWidgetSelectionShape) => void;
}

export function MapDrawingControls(props: IMapDrawingControlsProps) {
  const {
    onModeChange,
    mode,
    showIdentify,
    isMobile,
    shapes,
    activeShape,
    onActiveShapeChange,
  } = props;
  const { t } = useTranslation();

  /**
   * Reimposta la modalità di disegno quando il widget viene disabilitato.
   * Questo è necessario per evitare che l'utente possa disegnare o
   * eliminare geometrie quando il widget è disabilitato.
   */
  const { visuallyDisabled } = useFormItemEnabled();
  const disabledRef = useRef(visuallyDisabled);

  useEffect(() => {
    if (visuallyDisabled && !disabledRef.current) {
      onModeChange('view');
    }
    disabledRef.current = visuallyDisabled;
  }, [visuallyDisabled, onModeChange]);

  return (
    <DrawingControlsContainer>
      <Radio.Group
        buttonStyle="solid"
        disabled={visuallyDisabled}
        onChange={e => {
          onModeChange(e.target.value ?? 'view');
        }}
        value={mode}
      >
        <MapDrawingRadioButton
          isMobile={isMobile}
          label={t('mappa.controlli.visualizza', {
            defaultValue: 'Visualizza',
          })}
          value="view"
          icon={<EyeOutlined aria-hidden="true" />}
        />
        <MapDrawingRadioButton
          isMobile={isMobile}
          label={t('mappa.controlli.disegna', {
            defaultValue: 'Disegna',
          })}
          value="draw"
          icon={<EditOutlined aria-hidden="true" />}
        />
        <MapDrawingRadioButton
          isMobile={isMobile}
          label={t('mappa.controlli.elimina', {
            defaultValue: 'Elimina',
          })}
          value="delete"
          icon={<DeleteOutlined aria-hidden="true" />}
        />
        {showIdentify && (
          <MapDrawingRadioButton
            isMobile={isMobile}
            label={t('mappa.controlli.interroga', {
              defaultValue: 'Interroga',
            })}
            value="identify"
            icon={<SearchOutlined aria-hidden="true" />}
          />
        )}
      </Radio.Group>
      {mode === 'draw' && shapes.length > 1 && (
        <Radio.Group
          buttonStyle="solid"
          size="small"
          style={{ marginTop: 4 }}
          aria-label={t('map-widget.draw.shape.aria-label', {
            defaultValue: 'Tipo di geometria da disegnare',
          })}
          value={activeShape}
          onChange={e =>
            onActiveShapeChange(e.target.value as MapWidgetSelectionShape)
          }
        >
          {shapes.map(shape => (
            <MapDrawingRadioButton
              key={shape}
              isMobile={isMobile}
              label={getShapeLabel(t, shape)}
              value={shape}
              icon={getShapeIcon(shape)}
            />
          ))}
        </Radio.Group>
      )}
    </DrawingControlsContainer>
  );
}
